/**
 * What the hours cost.
 *
 * ---------------------------------------------------------------------
 * THE WEEKS COME FIRST, NOT THE RUNS
 *
 * A controller opening this on a Monday is not looking for a list of
 * things they have already done. They are asking one question — can I
 * cost last week yet — and the answer is usually no for a reason
 * somebody else has to fix. So the top of the page is the weeks, with
 * what is standing in the way of each, and the runs are underneath.
 *
 * A week that is blocked says why and by whom. "Three entries not
 * approved" is a message for a head of department; "Piotr Lewandowski
 * has hours and no rate" is a message for whoever does the hiring. A
 * blocked week that only said "not ready" would make somebody go and
 * find out, every Monday.
 *
 * ---------------------------------------------------------------------
 * THE DRAFT IS SHOWN BEFORE IT IS POSTED
 *
 * Computing produces a draft that has touched no ledger. It is displayed
 * with its departmental split and two buttons: post it, or throw it
 * away. If the number is wrong the fix is upstream — a timesheet, a
 * rate — and throwing the draft away costs nothing. Once posted, the
 * only way back is a reversal, which needs a reason and shows on the
 * P&L twice.
 *
 * ---------------------------------------------------------------------
 * WHO IS PAID WHAT IS BEHIND A SECOND CLICK
 *
 * The departmental totals load with the run. The per-person figures do
 * not, and they never will: every one of those calls writes an audit row
 * naming the reader. The same rule as the pay history on Personnel, for
 * the same reason — a log full of reads nobody meant to make is a log
 * nobody can use.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate, money, titleCase } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface ReadyWeek {
  week_starting: string;
  week_ending: string;
  entries: number;
  unapproved: number;
  staff: number;
  worked_minutes: number;
  worked_hours: string;
  staff_without_a_rate: number;
  already_costed: boolean;
}

interface Run {
  id: string;
  covers_from: string;
  covers_to: string;
  weeks: number;
  status: string;
  rounding_minutes: number;
  overtime_multiplier: string;
  staff_costed: number;
  entries_costed: number;
  total_minor: number;
  salaried_overtime_minutes: number;
  salaried_overtime_hours: string;
  entry_number: number | null;
  posted_at: string | null;
  reversed_at: string | null;
  reversed_reason: string | null;
}

interface DeptTotal {
  department_id: string;
  department_code: string;
  department: string;
  staff_costed: number;
  minutes_paid: number;
  minutes_overtime: number;
  wages_minor: number;
  overtime_minor: number;
  amount_minor: number;
}

interface Blocker {
  reason: string;
  detail: string | null;
  affected: number;
}

const hours = (minutes: number) => `${(minutes / 60).toFixed(1)}h`;

/**
 * A blocker, in the words of whoever has to act on it.
 *
 * The database returns a machine reason and a detail; the reason is what
 * turns "3" into a sentence. Written here rather than in SQL because it
 * is a label, and labels follow the club's vocabulary.
 */
function blockerText(b: Blocker): string {
  switch (b.reason) {
    case 'unapproved_time':
      return `${b.affected} time ${b.affected === 1 ? 'entry is' : 'entries are'} not approved yet`;
    case 'no_pay_rate':
      return `${b.affected} ${b.affected === 1 ? 'person has' : 'people have'} hours and no pay rate — ${b.detail}`;
    case 'rollup_department':
      return `${b.affected} ${b.affected === 1 ? 'person is' : 'people are'} filed under a department that cannot carry costs — ${b.detail}`;
    default:
      return `${b.reason}${b.detail ? ` — ${b.detail}` : ''} (${b.affected})`;
  }
}

export function LaborCostPage() {
  const { currency, atLeast } = useSession();
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<{ week: string; list: Blocker[] } | null>(null);

  const mayCost = atLeast('controller');

  const ready = useApi<{ weeks: ReadyWeek[] }>('/v1/labor/ready?weeks=10');
  const runs = useApi<{ runs: Run[] }>('/v1/labor/runs');

  const compute = useMutation(async (w: ReadyWeek) => {
    const r = await api<{ computed: boolean; run?: Run; blockers?: Blocker[] }>(
      '/v1/labor/runs',
      { method: 'POST', body: { from: w.week_starting, to: w.week_ending } },
    );
    if (r.computed && r.run) {
      setBlockers(null);
      setOpenRun(r.run.id);
    } else {
      setBlockers({ week: w.week_starting, list: r.blockers ?? [] });
    }
    ready.reload();
    runs.reload();
    return r;
  });

  if (ready.loading && !ready.data) return <Loading />;

  const weeks = ready.data?.weeks ?? [];
  const all = runs.data?.runs ?? [];
  const live = all.filter((r) => r.status !== 'reversed');

  const postedTotal = live
    .filter((r) => r.status === 'posted')
    .reduce((n, r) => n + Number(r.total_minor), 0);

  return (
    <>
      <h1>Labor Cost</h1>
      <ErrorBanner error={ready.error ?? runs.error ?? compute.error} />

      <p className="muted small">
        Approved hours priced against the rates on file, posted to the departmental P&amp;L as an
        accrual. Nothing here pays anybody — the debt lands on Accrued Payroll and your payroll
        processor clears it.
      </p>

      <div className="tiles">
        <Tile label="Weeks costed" value={String(live.filter((r) => r.status === 'posted').length)} />
        <Tile label="Posted to date" value={money(postedTotal, currency)} />
        <Tile
          label="Waiting to be costed"
          value={String(weeks.filter((w) => !w.already_costed).length)}
          note={weeks.some((w) => !w.already_costed && w.unapproved > 0) ? 'some need approval first' : undefined}
        />
      </div>

      {/* ============================================================ */}
      <Card title="The Weeks">
        {weeks.length === 0 ? (
          <Empty>No hours have been recorded yet, so there is nothing to cost.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Week</th>
                  <th className="num">People</th>
                  <th className="num">Hours</th>
                  <th>Standing in the way</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => {
                  const problems: string[] = [];
                  if (w.unapproved > 0) {
                    problems.push(
                      `${w.unapproved} ${w.unapproved === 1 ? 'entry' : 'entries'} not approved`,
                    );
                  }
                  if (w.staff_without_a_rate > 0) {
                    problems.push(
                      `${w.staff_without_a_rate} with no pay rate`,
                    );
                  }
                  return (
                    <tr key={w.week_starting}>
                      <td data-label="Week" className="cell--title">
                        {fmtDate(w.week_starting)} – {fmtDate(w.week_ending)}
                      </td>
                      <td data-label="People" className="num">
                        {w.staff}
                      </td>
                      <td data-label="Hours" className="num">
                        {hours(Number(w.worked_minutes))}
                      </td>
                      <td data-label="Standing in the way">
                        {w.already_costed ? (
                          <Status tone="good">Costed</Status>
                        ) : problems.length === 0 ? (
                          <Status tone="good">Ready</Status>
                        ) : (
                          <span className="muted small">{problems.join(' · ')}</span>
                        )}
                      </td>
                      <td className="cell--action">
                        {mayCost && !w.already_costed && (
                          <button
                            className="secondary small"
                            disabled={compute.busy}
                            onClick={() => compute.run(w)}
                          >
                            {compute.busy ? 'Costing…' : 'Cost this week'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* The 409 from the API, spelled out. The list is what somebody
            acts on, so it is not collapsed into "not ready". */}
        {blockers && blockers.list.length > 0 && (
          <div className="notice notice--warn">
            <strong>The week of {fmtDate(blockers.week)} cannot be costed yet.</strong>
            <ul>
              {blockers.list.map((b) => (
                <li key={b.reason}>{blockerText(b)}</li>
              ))}
            </ul>
            <p className="muted small">
              A week is costed all at once or not at all. Costing the part that works would leave
              the rest with nowhere to go — a second run cannot cover a week that is already
              spoken for.
            </p>
          </div>
        )}
      </Card>

      {/* ============================================================ */}
      <Card title="What Has Been Costed">
        {all.length === 0 ? (
          <Empty>Nothing has been costed yet.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>Covering</th>
                  <th className="num">People</th>
                  <th className="num">Cost</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {all.map((r) => (
                  <tr key={r.id} className={r.status === 'reversed' ? 'muted' : undefined}>
                    <td data-label="Covering" className="cell--title">
                      {fmtDate(r.covers_from)} – {fmtDate(r.covers_to)}
                      {r.weeks > 1 && <span className="muted small"> · {r.weeks} weeks</span>}
                    </td>
                    <td data-label="People" className="num">
                      {r.staff_costed}
                    </td>
                    <td data-label="Cost" className="num">
                      {money(r.total_minor, currency)}
                    </td>
                    <td data-label="State">
                      <Status
                        tone={
                          r.status === 'posted'
                            ? 'good'
                            : r.status === 'reversed'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {titleCase(r.status)}
                      </Status>
                      {r.entry_number && (
                        <span className="muted small"> · entry {r.entry_number}</span>
                      )}
                    </td>
                    <td className="cell--action">
                      <button
                        className="linkish"
                        onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
                      >
                        {openRun === r.id ? 'Close' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRun && (
        <RunDetail
          runId={openRun}
          mayCost={mayCost}
          onChanged={() => {
            ready.reload();
            runs.reload();
          }}
        />
      )}
    </>
  );
}

// =====================================================================
// One run
// =====================================================================

function RunDetail({
  runId,
  mayCost,
  onChanged,
}: {
  runId: string;
  mayCost: boolean;
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const [people, setPeople] = useState(false);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const { data, error, loading, reload } = useApi<{
    run: Run;
    departments: DeptTotal[];
    may_see_people: boolean;
  }>(`/v1/labor/runs/${runId}`, [runId]);

  const post = useMutation(async () => {
    const r = await api(`/v1/labor/runs/${runId}/post`, { method: 'POST' });
    reload();
    onChanged();
    return r;
  });

  const discard = useMutation(async () => {
    const r = await api(`/v1/labor/runs/${runId}`, { method: 'DELETE' });
    onChanged();
    return r;
  });

  const reverse = useMutation(async () => {
    const r = await api(`/v1/labor/runs/${runId}/reverse`, {
      method: 'POST',
      body: { reason },
    });
    setReversing(false);
    setReason('');
    reload();
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const { run, departments } = data;
  const overtime = departments.reduce((n, d) => n + Number(d.overtime_minor), 0);

  return (
    <Card title={`${fmtDate(run.covers_from)} – ${fmtDate(run.covers_to)}`}>
      <ErrorBanner error={error ?? post.error ?? discard.error ?? reverse.error} />

      {run.status === 'draft' && (
        <p className="muted small">
          A draft. Nothing has reached the ledger — look at the number, and if it is wrong the fix
          is a timesheet or a rate, not a correcting entry.
        </p>
      )}
      {run.status === 'reversed' && (
        <p className="muted small">
          Reversed {fmtDate(run.reversed_at)}: {run.reversed_reason}
        </p>
      )}

      <div className="tiles">
        <Tile label="Total" value={money(run.total_minor, currency)} />
        <Tile label="Overtime" value={money(overtime, currency)} />
        <Tile
          label="Rounding"
          value={run.rounding_minutes === 1 ? 'None' : `${run.rounding_minutes} min`}
          note={run.rounding_minutes === 1 ? 'the clock as it stood' : 'to the nearest, ties up'}
        />
        <Tile label="Entries" value={String(run.entries_costed)} />
      </div>

      {/* Hours somebody is owed and this cannot price. Named rather than
          shown as a zero — see migration 0143. */}
      {run.salaried_overtime_minutes > 0 && (
        <div className="notice notice--warn">
          <strong>{run.salaried_overtime_hours} hours of salaried overtime are not in this figure.</strong>
          <p className="muted small">
            Salaried staff marked non-exempt worked past forty hours. Those hours are owed, but the
            rate they are owed at depends on which method your payroll processor uses, and guessing
            would be worse than not answering. Settle them with your processor.
          </p>
        </div>
      )}

      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>Department</th>
              <th className="num">People</th>
              <th className="num">Hours</th>
              <th className="num">Wages</th>
              <th className="num">Overtime</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((d) => (
              <tr key={d.department_id}>
                <td data-label="Department" className="cell--title">
                  {d.department}
                </td>
                <td data-label="People" className="num">
                  {d.staff_costed}
                </td>
                <td data-label="Hours" className="num">
                  {hours(Number(d.minutes_paid))}
                </td>
                <td data-label="Wages" className="num">
                  {money(d.wages_minor, currency)}
                </td>
                <td data-label="Overtime" className="num">
                  {Number(d.overtime_minor) === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    money(d.overtime_minor, currency)
                  )}
                </td>
                <td data-label="Total" className="num">
                  {money(d.amount_minor, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.may_see_people && (
        <div className="actions">
          {!people ? (
            <button className="secondary small" onClick={() => setPeople(true)}>
              Show what each person cost
            </button>
          ) : (
            <button className="secondary small" onClick={() => setPeople(false)}>
              Hide
            </button>
          )}
        </div>
      )}

      {people && <People runId={runId} />}

      {mayCost && (
        <div className="actions">
          {run.status === 'draft' && (
            <>
              <button disabled={post.busy} onClick={() => post.run()}>
                {post.busy ? 'Posting…' : 'Post to the ledger'}
              </button>
              <button className="secondary" disabled={discard.busy} onClick={() => discard.run()}>
                Throw this draft away
              </button>
            </>
          )}

          {run.status === 'posted' &&
            (!reversing ? (
              <button className="secondary" onClick={() => setReversing(true)}>
                Reverse it
              </button>
            ) : (
              <>
                <label className="field">
                  <span>Why</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="A week's hours were wrong"
                  />
                  <div className="hint">
                    This is the only record of why the P&amp;L moved, so it is required.
                  </div>
                </label>
                <button disabled={!reason.trim() || reverse.busy} onClick={() => reverse.run()}>
                  {reverse.busy ? 'Reversing…' : 'Reverse it'}
                </button>
                <button className="secondary" onClick={() => setReversing(false)}>
                  Cancel
                </button>
              </>
            ))}
        </div>
      )}
    </Card>
  );
}

// =====================================================================
// Per person
// =====================================================================

function People({ runId }: { runId: string }) {
  const { currency } = useSession();
  const { data, error, loading } = useApi<{ people: any[] }>(
    `/v1/labor/runs/${runId}/people`,
    [runId],
  );

  if (loading && !data) return <Loading rows={1} />;

  return (
    <div>
      <ErrorBanner error={error} />
      <p className="muted small">
        This read is recorded — who opened it, when, and for which run. The figures themselves are
        never written to the log.
      </p>
      <div className="table">
        <table className="data">
          <thead>
            <tr>
              <th>Who</th>
              <th>Department</th>
              <th className="num">Hours</th>
              <th className="num">Overtime</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {(data?.people ?? []).map((p: any) => (
              <tr key={p.staff_member_id}>
                <td className="cell--title">
                  {p.display_name}
                  <span className="muted small"> · {p.job_title}</span>
                </td>
                <td className="muted">{p.department}</td>
                <td className="num">
                  {hours(Number(p.minutes_paid))}
                  {p.basis === 'salary' && <span className="muted small"> · salaried</span>}
                </td>
                <td className="num">
                  {Number(p.minutes_overtime) === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    hours(Number(p.minutes_overtime))
                  )}
                </td>
                <td className="num">{money(p.amount_minor, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
